import { D } from "@mobily/ts-belt";
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useLocation, useParams } from "react-router";
import { useTracking } from "../../hooks";
import ShopNotFound from "./components/ShopNotFound.jsx";
import { useShop, useShopProducts } from "./hooks/index.js";
import AccessoriesPage from "./AccessoriesPage/index.jsx";
import ProductPage from "./ProductPage.jsx";
import LegacyShop from "../Shop";
import ShopPage from "./ShopPage.jsx";
import { Influencer, withIncludesInfluencer } from "../../domain";

export default function ShopV2() {
  const { setTrackingCode } = useTracking();
  const { route, productId } = useParams();
  const location = useLocation();

  useEffect(() => {
    setTrackingCode();
  }, [setTrackingCode]);

  const isPreview = new URLSearchParams(location.search).has("preview");

  const {
    data: influencer,
    isLoading: isShopLoading,
    isError,
    error,
  } = useShop(route, { preview: isPreview });

  const shopProducts = useShopProducts(influencer);
  const isLoading = isShopLoading || !influencer;

  const isAccessories = route?.toLowerCase() === "accessories";

  // Compute a stable key for ProductPage that doesn't change on variant
  // selections. Variants share the same base product, so variant URL changes
  // (via history.replace) won't remount the component.
  const productKey = useMemo(() => {
    if (!productId) return productId;
    const all = [
      ...shopProducts.allProducts,
      ...shopProducts.customImageProducts,
    ];
    if (!all.length) return productId;
    const pid = Number.parseInt(productId, 10);
    const match = all.find(
      (p) =>
        p.product_id === pid || p._variants?.some((v) => v.product_id === pid),
    );
    return match ? String(match.product_id) : productId;
  }, [productId, shopProducts.allProducts, shopProducts.customImageProducts]);

  // Save & restore scroll position when toggling between shop and product views
  const shopScrollRef = useRef(0);
  const wasOnProduct = useRef(!!productId);
  const prevProductKey = useRef(productKey);
  const prevRoute = useRef(route);

  useLayoutEffect(() => {
    if (isAccessories) return;

    const isOnProduct = !!productId;
    const keyChanged = productKey !== prevProductKey.current;
    const routeChanged = route !== prevRoute.current;

    if (routeChanged) {
      // Different shop: reset saved scroll and go to top
      shopScrollRef.current = 0;
      window.scrollTo(0, 0);
    } else if (isOnProduct && !wasOnProduct.current) {
      // Shop → Product: save scroll position, scroll to top
      shopScrollRef.current = window.scrollY;
      window.scrollTo(0, 0);
    } else if (!isOnProduct && wasOnProduct.current) {
      // Product → Shop: restore saved scroll position
      window.scrollTo(0, shopScrollRef.current);
    } else if (isOnProduct && wasOnProduct.current && keyChanged) {
      // Product → different Product (e.g. recommendation): scroll to top
      window.scrollTo(0, 0);
    }
    // Variant change (productId changes but key stays same): no scroll change

    wasOnProduct.current = isOnProduct;
    prevProductKey.current = productKey;
    prevRoute.current = route;
  }, [productId, productKey, route, isAccessories]);

  if (isError) {
    return <ShopNotFound error={error} />;
  }

  if (isAccessories) {
    return (
      <AccessoriesPage
        route={route}
        influencer={influencer}
        shopProducts={shopProducts}
        isLoading={isShopLoading}
      />
    );
  }

  return (
    <>
      {/* Keep ShopPage mounted so scroll position and filter state survive
          navigation to a product page and back. */}
      <div style={productId ? { display: "none" } : undefined}>
        {influencer?.is_legacy_shop ? (
          <LegacyShop
            influencer={withIncludesInfluencer(
              Influencer.from(
                D.update(influencer, "products", (obj) =>
                  Object.values(obj).sort((a, b) => a.position - b.position),
                ),
              ),
            )}
          />
        ) : (
          <ShopPage
            route={route}
            influencer={influencer}
            shopProducts={shopProducts}
            isLoading={isShopLoading}
            isPreview={isPreview}
          />
        )}
      </div>

      {productId && (
        <ProductPage
          key={productKey}
          route={route}
          productId={productId}
          influencer={influencer}
          allProducts={shopProducts.allProducts}
          customImageProducts={shopProducts.customImageProducts}
          isLoading={isLoading}
          breadcrumb={location.state?.breadcrumb}
        />
      )}
    </>
  );
}
